import castArray from "lodash/fp/castArray";
import concat from "lodash/fp/concat";
import filter from "lodash/fp/filter";
import find from "lodash/fp/find";
import flatMap from "lodash/fp/flatMap";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import has from "lodash/fp/has";
import isNil from "lodash/fp/isNil";
import map from "lodash/fp/map";
import negate from "lodash/fp/negate";
import some from "lodash/fp/some";
import uniqBy from "lodash/fp/uniqBy";

import { getIsDisabled } from "utils/getFieldsWithIsDisabledValue";
import { getFlatFields } from "utils";

const hasDisplayIfValues =
  (name) =>
  ({ displayIf = {}, options = [] }) => {
    return (
      castArray(displayIf).some(has(name)) ||
      options.some(
        flow(getOr([], ["displayIf"]), castArray, some(has(name)))
      ) ||
      options.some(has(["isDisabledIf", name]))
    );
  };

export default function getFieldsToUpdate({
  name,
  value,
  customisations = [],
  userChoiceValues = {},
}) {
  const currentValue = userChoiceValues[name];

  // to ensure unsetting nested field values
  const nestedFields = flow(
    flatMap(get(["fields"])),
    find({ name }),
    getOr([], "options"),
    find({ value: currentValue }),
    getOr([], "fields"),
    flatMap(({ props = {}, ...field }) => [field, ...(props?.fields || [])]),
    flatMap(castArray),
    flatMap(({ options = [], ...field }) => [
      field,
      ...options.flatMap(getOr([], "fields")),
    ])
  )(customisations);

  return flow(
    getFlatFields,
    filter(hasDisplayIfValues(name)),
    filter((field) => {
      if (!userChoiceValues.hasOwnProperty(field.name)) {
        return false;
      }

      if (field?.displayIf) {
        const willDisplayField = castArray(field.displayIf).every(
          (displayIf) => {
            if (displayIf[name]?.includes) {
              return displayIf[name].includes.includes(value);
            }

            if (displayIf[name]?.notIncludes) {
              return !displayIf[name].notIncludes.includes(value);
            }

            return true;
          }
        );

        if (!willDisplayField) {
          return true;
        }
      }

      const selectedValue = userChoiceValues[field.name];

      const fieldIsDisabled = getIsDisabled(userChoiceValues)(
        field.isDisabledIf || {}
      );

      const checkboxOptions = fieldIsDisabled
        ? []
        : [
            { value: field.checkedValue, label: field.checkedLabel },
            { value: field.uncheckedValue, label: field.uncheckedLabel },
          ].filter(flow(get("value"), negate(isNil)));

      const availableOptions = [
        ...(field?.options || []),
        ...checkboxOptions,
      ].filter((option) => option.value === selectedValue);

      return (
        availableOptions.filter((option) => {
          const _displayIf = option?.displayIf || field?.isDisabledIf;

          if (!_displayIf) {
            return true;
          }

          return castArray(_displayIf).every((displayIf) => {
            if (displayIf[name]?.includes) {
              return displayIf[name].includes.includes(value);
            }

            if (displayIf[name]?.notIncludes) {
              return !displayIf[name].notIncludes.includes(value);
            }

            return true;
          });
        }).length === 0
      );
    }),

    concat(nestedFields),

    map((field) => {
      if (
        field.hasOwnProperty("defaultValue") &&
        field.hasOwnProperty("checkedValue") &&
        field.hasOwnProperty("uncheckedValue")
      ) {
        return { name: field.name, value: field.defaultValue };
      }

      return { name: field.name, value: null };
    }),

    concat([{ name, value }]),

    uniqBy(get("name"))
  )(customisations);
}
